import { useMemo, useCallback } from 'react';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../constants/pagination';

/**
 * Formats a date string to MM/DD/YYYY format
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string or 'N/A' if invalid
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

/**
 * Formats null or undefined values to 'N/A'
 * @param {*} value - The value to format
 * @returns {*} The value or 'N/A' if null/undefined
 */
export const formatNullValue = (value) => (value === null || value === undefined ? 'N/A' : value);

/**
 * Converts sort string to sortBy object format
 * @param {string} sort - Sort string in format 'columnId:direction'
 * @param {string} defaultSort - Default sort value if sort is empty
 * @returns {Object} SortBy object with id and desc properties
 */
export const getSortBy = (sort, defaultSort) => {
  if (!sort) {
    const [id, direction] = defaultSort.split(':');
    return { id, desc: direction === 'DESC' };
  }

  const [id, direction] = sort.split(':');
  return { id, desc: direction === 'DESC' };
};

/**
 * Hook to get sortBy object from sort string
 * @param {string} sort - Sort string in format 'columnId:direction'
 * @param {string} defaultSort - Default sort value if sort is empty
 * @returns {Object} SortBy object with id and desc properties
 */
export const useSortBy = (sort, defaultSort) => useMemo(() => getSortBy(sort, defaultSort), [sort, defaultSort]);

/**
 * Checks if any columns are sortable
 * @param {Array} columns - Array of column definitions
 * @returns {boolean} True if any column is sortable
 */
export const useHasSortableColumns = (columns) => useMemo(() => columns.some((column) => column.canSort), [columns]);

/**
 * Hook to create table handlers for pagination, sorting, and filtering
 * @param {Object} setters - Object containing state setters
 * @param {Function} setters.setCurrentPage - Function to set current page
 * @param {Function} setters.setPerPage - Function to set per page
 * @param {Function} setters.setSort - Function to set sort
 * @param {Function} setters.setFilterParams - Function to set filter params
 * @param {Object} initialFilterParams - Initial filter params for reset
 * @returns {Object} Object containing all handlers
 */
export const useTableHandlers = (setters, initialFilterParams) => {
  const { setCurrentPage, setPerPage, setSort, setFilterParams } = setters;

  const pageChangeHandler = useCallback(
    (page) => {
      setCurrentPage(page);
    },
    [setCurrentPage],
  );

  const perPageChangeHandler = useCallback(
    (newPerPage) => {
      setPerPage(newPerPage);
    },
    [setPerPage],
  );

  const applyFilterHandler = useCallback(
    (filterValues) => {
      setFilterParams(filterValues);
      setCurrentPage(DEFAULT_PAGE);
    },
    [setFilterParams, setCurrentPage],
  );

  const handleSortChange = useCallback(
    (columnId, isDesc) => {
      const direction = isDesc ? 'DESC' : 'ASC';
      const newSort = `${columnId}:${direction}`;
      setSort(newSort);
      setCurrentPage(DEFAULT_PAGE);
    },
    [setSort, setCurrentPage],
  );

  const resetFilterParams = useCallback(() => {
    setFilterParams(initialFilterParams);
    setCurrentPage(DEFAULT_PAGE);
  }, [setFilterParams, initialFilterParams, setCurrentPage]);

  return useMemo(
    () => ({
      pageChangeHandler,
      perPageChangeHandler,
      applyFilterHandler,
      handleSortChange,
      resetFilterParams,
    }),
    [pageChangeHandler, perPageChangeHandler, applyFilterHandler, handleSortChange, resetFilterParams],
  );
};

/**
 * Hook to create Redux-aware handlers for Exception Reporting filters
 * @param {Object} params - Parameters object
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Object} params.reduxState - Redux state for the tab (unmappedDataFeed or missingDataFeed)
 * @param {Function} params.setFilterAction - Redux action to set filters
 * @param {Function} params.resetFilterAction - Redux action to reset filters
 * @param {Object} params.initialFilterParams - Initial filter params for reset
 * @param {string} params.sortDefaultValue - Default sort value
 * @param {Function} params.setFilterParams - Function to set filter params
 * @param {Function} params.setSort - Function to set sort
 * @param {Function} params.setCurrentPage - Function to set current page
 * @param {Function} params.setPerPage - Function to set per page
 * @returns {Object} Object containing all Redux-aware handlers
 */
export const useExceptionReportingHandlers = ({
  dispatch,
  reduxState,
  setFilterAction,
  resetFilterAction,
  initialFilterParams,
  sortDefaultValue,
  setFilterParams,
  setSort,
  setCurrentPage,
  setPerPage,
}) => {
  const applyFilterHandler = useCallback(
    (filterValues) => {
      dispatch(
        setFilterAction({
          ...reduxState,
          ...filterValues,
          sort: reduxState.sort || sortDefaultValue,
          pageNumber: DEFAULT_PAGE,
          rowsPerPage: DEFAULT_PER_PAGE,
        }),
      );
      setFilterParams(filterValues);
      setCurrentPage(DEFAULT_PAGE);
    },
    [dispatch, reduxState, setFilterAction, sortDefaultValue, setFilterParams, setCurrentPage],
  );

  const resetFilterParams = useCallback(() => {
    setFilterParams(initialFilterParams);
    dispatch(resetFilterAction());
    setCurrentPage(DEFAULT_PAGE);
  }, [dispatch, resetFilterAction, setFilterParams, initialFilterParams, setCurrentPage]);

  const handleSortChangeWithRedux = useCallback(
    (columnId, isDesc) => {
      const direction = isDesc ? 'DESC' : 'ASC';
      const newSort = `${columnId}:${direction}`;
      dispatch(
        setFilterAction({
          ...reduxState,
          sort: newSort,
          pageNumber: DEFAULT_PAGE,
          rowsPerPage: DEFAULT_PER_PAGE,
        }),
      );
      setSort(newSort);
      setCurrentPage(DEFAULT_PAGE);
    },
    [dispatch, reduxState, setFilterAction, setSort, setCurrentPage],
  );

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      dispatch(
        setFilterAction({
          ...reduxState,
          pageNumber: page,
        }),
      );
    },
    [dispatch, reduxState, setFilterAction, setCurrentPage],
  );

  const handlePerPageChange = useCallback(
    (newPerPage) => {
      setPerPage(newPerPage);
      dispatch(
        setFilterAction({
          ...reduxState,
          rowsPerPage: newPerPage,
          pageNumber: DEFAULT_PAGE,
        }),
      );
    },
    [dispatch, reduxState, setFilterAction, setPerPage, setCurrentPage],
  );

  return useMemo(
    () => ({
      applyFilterHandler,
      resetFilterParams,
      handleSortChangeWithRedux,
      handlePageChange,
      handlePerPageChange,
    }),
    [applyFilterHandler, resetFilterParams, handleSortChangeWithRedux, handlePageChange, handlePerPageChange],
  );
};
